import { useCallback, useEffect, useRef, useState } from "react";
import { Activity, Database, Cpu, Sparkles, Loader2, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { api } from "../lib/nailApi";
import { useI18n } from "../i18n/I18nContext";

/**
 * AdminOpsBadge — small pill in the admin header showing live status of
 * DB / OpenAI / Anthropic. Opens a popover with a "Đánh thức DB" button.
 *
 * Auto-polls every 60s while mounted. Manual refresh on popover open.
 */
export default function AdminOpsBadge() {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [waking, setWaking] = useState(false);
  const [wakeResult, setWakeResult] = useState(null);
  const boxRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ops/health");
      setStatus(data);
    } catch (_e) {
      setStatus({
        db: { ok: false, error: "network" },
        openai: { ok: false, error: "network" },
        anthropic: { ok: false, error: "network" },
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const wake = useCallback(async () => {
    setWaking(true);
    setWakeResult(null);
    try {
      const { data } = await api.post("/admin/ops/wake-db");
      setWakeResult(data);
      // Refresh full status after a successful wake
      if (data?.ok) refresh();
    } catch (_e) {
      setWakeResult({ ok: false, error: "network" });
    } finally {
      setWaking(false);
    }
  }, [refresh]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    const onClick = (e) => {
      if (open && boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const overall = (() => {
    if (!status) return "loading";
    const { db, openai, anthropic } = status;
    if (db?.ok && openai?.ok) return "ok"; // Anthropic is fallback — not critical
    if (db?.ok) return "warn"; // DB up but LLM providers down
    return "down";
  })();

  const pillColor = {
    ok: "bg-emerald-100 text-emerald-800 border-emerald-200",
    warn: "bg-amber-100 text-amber-800 border-amber-200",
    down: "bg-red-100 text-red-800 border-red-200",
    loading: "bg-gray-100 text-gray-600 border-gray-200",
  }[overall];

  const overallLabel = {
    ok: lang === "en" ? "Systems OK" : "Hệ thống OK",
    warn: lang === "en" ? "LLM issue" : "AI lỗi",
    down: lang === "en" ? "DB down" : "DB tắt",
    loading: lang === "en" ? "Checking…" : "Đang kiểm tra…",
  }[overall];

  return (
    <div className="relative" ref={boxRef} data-testid="admin-ops-badge">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold px-2.5 py-1 rounded-full border ${pillColor} hover:opacity-90 transition`}
        data-testid="admin-ops-badge-toggle"
        title={lang === "en" ? "System status" : "Trạng thái hệ thống"}
      >
        <Activity className="w-3 h-3" />
        {overallLabel}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 bg-white border border-espresso/10 rounded-2xl shadow-xl z-50 p-4 space-y-3"
          data-testid="admin-ops-badge-panel"
        >
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-espresso-soft">
              {lang === "en" ? "System Status" : "Trạng thái hệ thống"}
            </div>
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              className="text-xs text-espresso-soft hover:text-espresso disabled:opacity-50"
              data-testid="admin-ops-refresh"
            >
              {loading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                lang === "en" ? "Refresh" : "Làm mới"
              )}
            </button>
          </div>

          <StatusRow
            icon={<Database className="w-4 h-4" />}
            label={lang === "en" ? "Postgres" : "Cơ sở dữ liệu"}
            check={status?.db}
          />
          <StatusRow
            icon={<Cpu className="w-4 h-4" />}
            label={lang === "en" ? "OpenAI (GPT-5.2)" : "OpenAI (GPT-5.2)"}
            check={status?.openai}
          />
          <StatusRow
            icon={<Sparkles className="w-4 h-4" />}
            label={lang === "en" ? "Anthropic (fallback)" : "Anthropic (dự phòng)"}
            check={status?.anthropic}
          />

          <div className="pt-2 border-t border-espresso/10">
            <button
              type="button"
              onClick={wake}
              disabled={waking}
              className="w-full inline-flex items-center justify-center gap-2 bg-espresso text-linen rounded-full px-4 py-2 text-xs font-semibold hover:bg-espresso-hover disabled:opacity-60"
              data-testid="admin-ops-wake-db"
            >
              {waking ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {lang === "en" ? "Waking…" : "Đang đánh thức…"}</>
              ) : (
                <><Database className="w-3.5 h-3.5" /> {lang === "en" ? "Wake Database" : "Đánh thức DB"}</>
              )}
            </button>
            {wakeResult && (
              <div
                className={`mt-2 text-xs ${wakeResult.ok ? "text-emerald-700" : "text-red-700"}`}
                data-testid="admin-ops-wake-result"
              >
                {wakeResult.ok
                  ? (lang === "en"
                    ? `DB awake — ${wakeResult.latency_ms}ms`
                    : `DB đã sẵn sàng — ${wakeResult.latency_ms}ms`)
                  : (lang === "en"
                    ? `Failed: ${wakeResult.error || "unknown"}`
                    : `Thất bại: ${wakeResult.error || "không rõ"}`)}
              </div>
            )}
          </div>

          <p className="text-[11px] text-espresso-soft leading-relaxed">
            {lang === "en"
              ? "Wake the DB before high-traffic events to avoid the 5s cold-start fallback message on the homepage chat."
              : "Đánh thức DB trước sự kiện đông người để tránh thông báo \"tạm gián đoạn\" trên chat trang chủ."}
          </p>
        </div>
      )}
    </div>
  );
}

function StatusRow({ icon, label, check }) {
  const { lang } = useI18n();
  const state = !check ? "loading" : check.ok ? "ok" : "down";
  const IconEl = { ok: CheckCircle2, warn: AlertTriangle, down: XCircle, loading: Loader2 }[state];
  const color = {
    ok: "text-emerald-600",
    down: "text-red-600",
    loading: "text-gray-400",
  }[state];
  return (
    <div className="flex items-center justify-between text-sm" data-testid={`admin-ops-row-${label}`}>
      <div className="flex items-center gap-2 text-espresso">
        <span className="text-espresso-soft">{icon}</span>
        <span className="font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        {check?.latency_ms != null && (
          <span className="text-xs text-espresso-soft tabular-nums">{check.latency_ms}ms</span>
        )}
        {check && !check.ok && check.error && (
          <span className="text-[10px] text-red-600 truncate max-w-[100px]" title={check.error}>
            {check.error}
          </span>
        )}
        <IconEl className={`w-4 h-4 ${color} ${state === "loading" ? "animate-spin" : ""}`} />
      </div>
    </div>
  );
}
